(() => {
  const UTM_KEYS = [
    "utm_id",
    "utm_term",
    "utm_content",
    "utm_campaign",
    "utm_medium",
    "utm_source",
  ];

  // The target field id can be overridden via the script's own src query
  // string, e.g. <script src="https://.../utm-forms.v1.js?selectorid=abc">.
  // Falls back to the legacy default when not provided.
  const readSelectorId = () => {
    const fromUrl = (url) => {
      try {
        return new URL(url, window.location.href).searchParams.get("selectorid");
      } catch {
        return null;
      }
    };

    let id = document.currentScript?.src ? fromUrl(document.currentScript.src) : null;
    if (!id) {
      for (const s of document.querySelectorAll("script[src*='selectorid']")) {
        id = fromUrl(s.src);
        if (id) break;
      }
    }
    return (id ?? "form-field-metadata").replace(/^["']|["']$/g, "");
  };

  const SELECTOR = `input[type='hidden'][id='${readSelectorId()}']`;
  const STORAGE_KEY = "tochat_metadata";
  const metadata = {};

  const writeOne = (input) => {
    input.value = JSON.stringify(metadata);
  };

  const modifyMetadata = (obj) => {
    Object.assign(metadata, obj);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
    } catch {}
    document.querySelectorAll(SELECTOR).forEach(writeOne);
  };

  // Restore prior metadata so values like UTM / fbc survive across pages.
  try {
    Object.assign(metadata, JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"));
  } catch {}

  const params = new URLSearchParams(window.location.search);

  // A URL carrying any UTM param is a new campaign entry: clear the whole
  // stored UTM set first so keys absent from this URL don't leak in from a
  // previous campaign. A URL with no UTM params keeps the stored set.
  const utmOverlay = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value !== null) utmOverlay[key] = value;
  }
  if (Object.keys(utmOverlay).length > 0) {
    for (const key of UTM_KEYS) delete metadata[key];
  }

  const readCookie = (name) =>
    document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"))?.[1] ??
    "";

  const readFb = () => {
    const out = {};
    const fbp = readCookie("_fbp");
    const fbc = readCookie("_fbc");
    if (fbp) out.fbp = fbp;
    if (fbc) out.fbc = fbc;
    return out;
  };

  // fbc fallback: if URL has ?fbclid= and the pixel hasn't set _fbc and we
  // have no stored fbc, build one ourselves so we can still match later.
  const fbUpdates = readFb();
  const fbclid = params.get("fbclid");
  if (!fbUpdates.fbc && !metadata.fbc && fbclid) {
    fbUpdates.fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  // Google Ads click ids: gclid (standard), plus gbraid/wbraid for iOS app /
  // web campaigns where gclid isn't available. Prefer the URL param; fall back
  // to the _gcl_aw cookie gtag sets (format: GCL.<timestamp>.<gclid>).
  const readGoogle = () => {
    const out = {};
    for (const key of ["gclid", "gbraid", "wbraid"]) {
      const value = params.get(key);
      if (value !== null) out[key] = value;
    }
    if (!out.gclid && !metadata.gclid) {
      const cookieGclid = readCookie("_gcl_aw").match(/^GCL\.\d+\.(.+)$/)?.[1];
      if (cookieGclid) out.gclid = decodeURIComponent(cookieGclid);
    }
    return out;
  };
  const googleUpdates = readGoogle();

  const eventId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);

  modifyMetadata({
    ...utmOverlay,
    ...fbUpdates,
    ...googleUpdates,
    user_agent: navigator.userAgent,
    event_source_url: window.location.href,
    landingPageUrl: window.location.origin + window.location.pathname,
    event_id: eventId,
  });

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(SELECTOR)) writeOne(node);
        node.querySelectorAll?.(SELECTOR).forEach(writeOne);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  const gaIntervalId = setInterval(() => {
    const gaClientId =
      document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/)?.[1] ?? null;
    if (gaClientId) {
      modifyMetadata({ g4_lead_id: gaClientId });
      clearInterval(gaIntervalId);
    }
  }, 200);

  const fbIntervalId = setInterval(() => {
    const updates = readFb();
    if (updates.fbp) {
      modifyMetadata(updates);
      clearInterval(fbIntervalId);
    }
  }, 200);
})();
